import _ from 'lodash';
import moment from 'moment';
import { isSameOrAfter } from '@sharedModules/data/utils/dates-utils';
import { yearMonthDayFormat } from '@enums/date-formats';
import { scenarioResults } from '@enums/source-types';
import {
  overrideSetExpiryDateEnabled,
  overrideSetExpiryDateDaysAfterGoLive,
} from '@enums/feature-flags';
import overrideTypes from '@enums/override-types';
import transitionPeriodOverrideStates from '@enums/transition-period-override-states';

/**
 * Determines the reference price to use when creating or updating a price override.
 *
 * This function selects the appropriate reference price based on the optimisation engine used:
 * - If XEO (Cross-Elasticity Engine Optimisation) is enabled and was the last engine to run,
 *   returns the cross-elasticity price.
 * - Otherwise, returns the optimised price from SEO (Strategic Engine Optimisation) if available,
 *   falling back to the scenario price.
 *
 * The reference price is stored with the override to track which price was in effect when
 * the override was created, allowing for price change tracking and analysis.
 *
 * @param {boolean} xeoEnabled - Indicates if XEO (Cross-Elasticity Engine Optimisation) is enabled.
 * @param {Object} item - The product item containing price information.
 * @param {string} [item.lastEngineSource] - The source of the last engine calculation ('crossElasticity' or other).
 * @param {number} [item.optimizedPrice] - The optimised price from SEO. Does not consider promotion or previous overrides.
 * @param {number} [item.scenarioPrice] - The scenario price, used as fallback.
 *        This is the last engine final price from either SEO or XEO if both are enabled.
 * @param {number} [item.crossElasticityPrice] - The optimised price from XEO when enabled.
 * @param {boolean} [ignoreSEOScenarioPrice=false] - If true, ignores scenario price fallback.
 * @returns {number|null} The reference price to use for the override.
 */
const getReferencePriceAtOverride = (xeoEnabled, item, ignoreSEOScenarioPrice = false) => {
  // If the override is being created on top of an active transition period override, use the
  // transition period override price as the reference price.
  if (
    _.get(item, 'overridePrice.overrideType') === overrideTypes.transitionPeriod &&
    _.get(item, 'transitionPeriodOverrideMetadata.state') === transitionPeriodOverrideStates.active
  ) {
    return _.get(item, 'transitionPeriodOverrideMetadata.price');
  }
  const lastEngineSource = _.get(item, 'lastEngineSource');
  const seoOptimizedPrice = _.get(item, 'optimizedPrice', null);
  const scenarioPrice = _.get(item, 'scenarioPrice', null);
  const xeoPrice = _.get(item, 'crossElasticityPrice', null);

  // XEO takes priority if enabled and was the last engine to run
  if (xeoEnabled && lastEngineSource === scenarioResults.crossElasticity) {
    return xeoPrice;
  }

  // Use SEO price if available, otherwise fallback to scenario price (unless ignored)
  return ignoreSEOScenarioPrice ? seoOptimizedPrice : seoOptimizedPrice || scenarioPrice;
};

/**
 * Validates and returns an override price if it is still active.
 *
 * If no effective/expiry dates are specified, the override is permanent.
 * If dates are specified, the override is valid only if the go-live date
 * is before the expiry date.
 *
 * @param {Object} overridePrice - The override price object.
 * @param {string} [overridePrice.effectiveDate] - Start date (YYYY-MM-DD).
 * @param {string} [overridePrice.expiryDate] - End date (YYYY-MM-DD).
 * @param {number} overridePrice.price - The override price.
 * @param {string} goLiveDate - The product go-live date (YYYY-MM-DD).
 * @returns {number|null} The price if valid and not expired, otherwise null.
 */
const getOverridePrice = (overridePrice, goLiveDate) => {
  const { effectiveDate, expiryDate, price } = overridePrice;

  // Permanent override if no expiry dates are specified
  if (!effectiveDate || !expiryDate) {
    return price;
  }

  // Check if override has expired relative to go-live date
  const goLiveMoment = moment(goLiveDate, yearMonthDayFormat, true);
  const expiryMoment = moment(expiryDate, yearMonthDayFormat, true);
  const hasExpired = isSameOrAfter(goLiveMoment, expiryMoment);

  return hasExpired ? null : price;
};

/**
 * Determines the scenario price for a product based on override, promotion, and engine status.
 *
 * Price selection priority:
 * 1. If usePromoPriceOnSmartOverride is enabled: return intentionPrice immediately
 * 2. If on promotion: overridePrice > intentionPrice
 * 3. Otherwise: overridePrice > enginePrice (SEO/XEO) > intentionPrice
 *
 * @param {Object} params - Parameters object.
 * @param {Object} params.item - Product item with pricing and promotion data.
 * @param {string} params.goLiveDate - Product go-live date (YYYY-MM-DD).
 * @param {number} params.intentionPrice - Base intention price.
 * @param {boolean} params.usePromoPriceOnSmartOverride - Force use of intention price on overrides.
 * @param {boolean} params.xeoEnabled - Whether cross-elasticity engine is enabled.
 * @returns {number} The computed scenario price.
 */
const computeScenarioPrice = ({
  item,
  goLiveDate,
  intentionPrice,
  usePromoPriceOnSmartOverride,
  xeoEnabled,
}) => {
  if (usePromoPriceOnSmartOverride) {
    return intentionPrice;
  }

  // Check promotion status
  const hasActiveOrUpcomingPromotion =
    _.get(item, 'isOnPromotion') || _.get(item, 'hasUpcomingPromotion');
  const overridePrice = getOverridePrice(_.get(item, 'overridePrice', {}), goLiveDate);

  // Priority logic: override > (engine price or intention price)
  if (hasActiveOrUpcomingPromotion) {
    return overridePrice || intentionPrice;
  }

  // Ignore SEO scenario price when getting reference price.
  // When optimised price does not exist, fall back to intention price (handled below).
  const referencePrice = getReferencePriceAtOverride(xeoEnabled, item, true);
  return overridePrice || referencePrice || intentionPrice;
};

/**
 * Calculates smart override date fields with automatic expiry based on go-live date.
 *
 * Smart overrides automatically set:
 * - Effective date: current date (today)
 * - Expiry date: go-live date + configurable number of days
 *
 * This function is only active when the 'overrideSetExpiryDateEnabled' feature flag is enabled.
 * Returns an empty object if the feature is disabled or no go-live date is provided.
 *
 * @param {Object} params - Parameters object.
 * @param {string} params.goLiveDate - The product go-live date in YYYY-MM-DD format.
 * @param {Object} params.toggleLogic - Feature flag configuration object.
 * @param {boolean} params.toggleLogic.overrideSetExpiryDateEnabled - Enables smart override date calculation.
 * @param {number} params.toggleLogic.overrideSetExpiryDateDaysAfterGoLive - Number of days after go-live for expiry.
 * @returns {Object} Smart override fields object, or empty object if feature is disabled.
 */
const getSmartOverrideFields = ({ goLiveDate, toggleLogic }) => {
  if (!toggleLogic[overrideSetExpiryDateEnabled] || !goLiveDate) {
    return {};
  }
  const daysAfterGoLive = toggleLogic[overrideSetExpiryDateDaysAfterGoLive];
  const effectiveDate = moment().format(yearMonthDayFormat);
  const effectiveTimestamp = moment().unix();
  const expiryDate = moment(goLiveDate)
    .add(daysAfterGoLive, 'days')
    .format(yearMonthDayFormat);
  const expiryTimestamp = moment(expiryDate, yearMonthDayFormat).unix();

  return {
    effectiveDate,
    effectiveTimestamp,
    expiryDate,
    expiryTimestamp,
    isSmart: true,
  };
};

export { computeScenarioPrice, getReferencePriceAtOverride, getSmartOverrideFields };
